/* DEPENDENCY NOTE — chartmind-translate (Query Manager / llmGlobalPrompts)
 * This file depends on the Firestore document:
 *   llmGlobalPrompts/chartmind-translate
 *
 * Despite the name, this is the platform's shared clinical-conversation translator —
 * used by CareStart voice/text paths (and any caller of translateViaChartmindTranslateQuery),
 * not ChartMind-specific. See docs/STRATEGIC_COHERENCE_LEDGER.md SCL-037.
 *
 * Repo seed (verbatim from caremap2020): firebase/seed/llmGlobalPrompts.translation.json
 * Reseed: node scripts/seedTranslationPrompts.js --project <id>
 *
 * callLLMAPI composes its system prompt from the global-layer fields
 * systemPrompt | globalPrompt | contextProvided | responseFormat and requires a
 * `model`. If that document is missing, OR all of those prompt fields are empty,
 * OR `model` is unset, callLLMAPI throws "No prompt content is available for
 * \"chartmind-translate\"" and ALL translation in /carestart3 silently falls back
 * to English with no user-visible error. This caused a production incident on
 * 2026-06-20 (the document was omitted from the 2026-06-19 llmGlobalPrompts
 * seeding run). NOTE: the runtime does NOT gate on an `active` field — content
 * presence + `model` are what matter.
 *
 * When deploying to a new Firebase project or environment, this document MUST be
 * seeded before /carestart3 is live. See also:
 *   CARESTART3_AUDIT_REPORT.md (root) -> "Prompt 1 preview"
 *   CARESTART3_PIPELINE_AUDIT.md (root) -> "CI Gap"
 */

/**
 * REST / Query Manager translation (chartmind-translate).
 * Shared by ChartMind and any feature that needs string-in/string-out translation.
 */

import { llmProviderService } from '../llm/llmProviderService';

const parseTranslationChunk = (chunk) => {
  const c = (chunk || '').trim();
  if (!c) return '';

  if (c.startsWith('{')) {
    try {
      const o = JSON.parse(c);
      if (o && typeof o.translation === 'string') return o.translation.trim();
    } catch (e) {
      console.warn('[restTranslation] Failed to parse single translation JSON object', e);
    }
  }

  const parts = [];
  let i = 0;
  while (i < c.length) {
    const start = c.indexOf('{', i);
    if (start < 0) break;
    let depth = 0;
    let j = start;
    for (; j < c.length; j += 1) {
      const ch = c[j];
      if (ch === '{') depth += 1;
      else if (ch === '}') {
        depth -= 1;
        if (depth === 0) {
          const slice = c.slice(start, j + 1);
          try {
            const o = JSON.parse(slice);
            if (o && typeof o.translation === 'string') parts.push(o.translation.trim());
          } catch (e) {
            if (slice.includes('"translation"')) {
              console.warn('[restTranslation] Malformed translation JSON in chunk', e);
            }
          }
          i = j + 1;
          break;
        }
      }
    }
    if (j >= c.length) break;
  }

  if (parts.length > 0) return parts.join(' ');
  return c;
};

/**
 * @param {unknown} raw - LLM output string or { translation } or { output: string }
 * @returns {string}
 */
export const normalizeTranslationLLMOutput = (raw) => {
  if (raw == null) return '';
  if (typeof raw === 'object' && raw !== null && typeof raw.translation === 'string') {
    return raw.translation.trim();
  }

  const s =
    typeof raw === 'string'
      ? raw.trim()
      : String(raw?.output ?? raw?.content ?? raw?.text ?? '').trim();

  if (!s) return '';

  const fencedBodies = [];
  const fenceRe = /```(?:json)?\s*([\s\S]*?)```/gi;
  let m;
  while ((m = fenceRe.exec(s)) !== null) {
    fencedBodies.push(m[1].trim());
  }

  const chunks = fencedBodies.length > 0 ? fencedBodies : [s];
  const texts = chunks.map((chunk) => parseTranslationChunk(chunk)).filter(Boolean);
  const joined = texts.join(' ').trim();
  if (joined) return joined;

  return parseTranslationChunk(s);
};

/**
 * @param {unknown} result
 * @returns {boolean}
 */
export function isTranslationFailure(result) {
  return result != null && typeof result === 'object' && result.translationFailed === true;
}

/**
 * Call llmGlobalPrompts/chartmind-translate via callLLMAPI.
 *
 * Despite the promptId, this is the platform's shared clinical-conversation translator —
 * used by CareStart voice, not ChartMind-specific. See STRATEGIC_COHERENCE_LEDGER.md SCL-037.
 *
 * @param {string} userInput - Full user message for chartmind-translate
 * @param {Object} [options]
 * @param {string} [options.module='chartmind']
 * @param {string} [options.featureName='translation']
 * @param {boolean} [options.returnFailureSignal=false] - When true, returns a typed failure
 *   object instead of throwing (CareStart3 only; ChartMind and other callers keep throw-on-error).
 * @returns {Promise<string|{translationFailed: true, reason: string, originalText: string}>}
 *   Plain translated text on success, or typed failure when returnFailureSignal is true.
 */
export const translateViaChartmindTranslateQuery = async (userInput, options = {}) => {
  const { module = 'chartmind', featureName = 'translation', returnFailureSignal = false } = options;
  try {
    const response = await llmProviderService.callLLMWithQuery('chartmind-translate', userInput, {
      module,
      featureName,
    });
    const raw =
      typeof response === 'string'
        ? response
        : (response?.output ?? response?.content ?? response?.text ?? '');
    return normalizeTranslationLLMOutput(raw);
  } catch (error) {
    if (returnFailureSignal) {
      return {
        translationFailed: true,
        reason: error?.message ?? 'unknown',
        originalText: userInput,
      };
    }
    throw error;
  }
};
